/**
 * CreatorKartotekaFieldsModal — modal edycji pól/layoutów/widoków kartoteki „Z Kreatora"
 * wprost z ekranu „Kartoteki" (panel ITE), bez skoku do edytora szablonu.
 *
 * Bliźniak `ManageFieldsModal` (kartoteki systemowe): ten sam kształt (Modal 2xl, tytuł
 * „Pola kartoteki: <nazwa>"), ale zamiast `KartotekaFieldsManager` reużywa panel edytora
 * szablonu `NewTableKartotekaPanel` (3 zakładki: Pola / Layouty formularzy / Widoki list).
 *
 * Źródło danych = szablon Kreatora (nie endpoint /definition):
 *   - GET  /admin/module-templates/{code}             → pełna definicja (encje)
 *   - PUT  /admin/module-templates/{code}/structure   → zapis pól (replace-all encji)
 *
 * Zakładka „Pola" edytuje `entity.fields` w lokalnym stanie i wymaga jawnego zapisu
 * (przycisk „Zapisz" → PUT structure). Zakładka „Layouty formularzy" (biblioteka widoków
 * kartoteki, adresowana UUID encji) zapisuje się SAMA przez własne API i jest aktywna tylko
 * gdy nie ma niezapisanych zmian struktury — dokładnie jak w edytorze szablonu.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModuleTemplateDefinition, type ModuleTemplateEntityDefinition } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { NewTableKartotekaPanel } from '@/pages/ModuleTemplateEditor';

import { Modal } from '../Modal';
import { Skeleton } from '../Skeleton';

/** Kartoteka „Z Kreatora" wybrana do edycji pól (encja szablonu). */
export interface CreatorKartotekaTarget {
  templateCode: string;
  entitySlug: string;
  name: string;
}

/**
 * Przepisuje sortOrder = indeks na encjach, polach i kolumnach widoków przed zapisem.
 * Lustro `normalizeStructure` z edytora szablonu — po przeciągnięciu/dodaniu pola indeks
 * tablicy jest źródłem prawdy, a nie (stały) sortOrder z GET.
 */
function normalizeStructure(
  entities: ModuleTemplateEntityDefinition[],
): ModuleTemplateEntityDefinition[] {
  return entities.map((entity, ei) => ({
    ...entity,
    sortOrder: ei,
    fields: entity.fields.map((field, fi) => ({ ...field, sortOrder: fi })),
    views: entity.views.map((view, vi) => ({
      ...view,
      sortOrder: vi,
      listConfig: view.listConfig
        ? {
            ...view.listConfig,
            columns: [...view.listConfig.columns]
              .sort((a, b) => a.order - b.order)
              .map((col, ci) => ({ ...col, order: ci })),
          }
        : view.listConfig,
    })),
  }));
}

export function CreatorKartotekaFieldsModal({
  target,
  onClose,
}: {
  target: CreatorKartotekaTarget | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  // Parytet z edytorem: strukturę edytuje tylko projektant. Bez roli → panel read-only.
  const canDesign = hasPermission('module_builder.designer');

  const templateCode = target?.templateCode;
  const entitySlug = target?.entitySlug;

  const detailQuery = useQuery<ModuleTemplateDefinition>({
    // Ten sam klucz co edytor szablonu — współdzielimy cache i inwalidacje.
    queryKey: ['admin', 'module-templates', templateCode],
    queryFn: async () => (await api.get(`/admin/module-templates/${templateCode}`)).data,
    enabled: target !== null && Boolean(templateCode),
    retry: false,
  });

  // Edytowalna kopia encji szablonu (raz po fetchu). `dirty` bramkuje przycisk „Zapisz".
  const [entities, setEntities] = useState<ModuleTemplateEntityDefinition[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (detailQuery.data) {
      setEntities(detailQuery.data.entities);
      setDirty(false);
    }
  }, [detailQuery.data]);

  // Zamknięcie / zmiana celu — wyczyść stan, żeby przy kolejnym otwarciu nie mignąć starą encją.
  useEffect(() => {
    if (!target) {
      setEntities([]);
      setDirty(false);
    }
  }, [target]);

  const entity = entities.find((e) => e.slug === entitySlug) ?? null;

  const saveMutation = useMutation({
    mutationFn: async (payload: ModuleTemplateEntityDefinition[]) =>
      (await api.put(`/admin/module-templates/${templateCode}/structure`, { entities: payload }))
        .data,
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('adminKartoteki.creatorModal.saved') });
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates', templateCode] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'kartoteki'] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('adminKartoteki.creatorModal.saveError') });
    },
  });

  const updateFields = (fields: ModuleTemplateEntityDefinition['fields']) => {
    if (!entity) return;
    setEntities((prev) => prev.map((e) => (e.slug === entity.slug ? { ...e, fields } : e)));
    setDirty(true);
  };

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={target ? t('adminKartoteki.creatorModal.title', { name: target.name }) : ''}
      description={t('adminKartoteki.creatorModal.description')}
      size="2xl"
      fixedHeight
      expandable
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('adminKartoteki.creatorModal.close')}
          </Button>
          {canDesign && (
            <Button
              onClick={() => saveMutation.mutate(normalizeStructure(entities))}
              disabled={!dirty || saveMutation.isPending}
              loading={saveMutation.isPending}
              data-testid="creator-kartoteka-save"
            >
              {t('adminKartoteki.creatorModal.save')}
            </Button>
          )}
        </>
      }
    >
      {detailQuery.isLoading && <Skeleton className="h-64 w-full" />}

      {detailQuery.isError && (
        <div
          role="alert"
          className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
        >
          {t('adminKartoteki.creatorModal.loadError')}
        </div>
      )}

      {!detailQuery.isLoading && !detailQuery.isError && !entity && detailQuery.data && (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {t('adminKartoteki.creatorModal.entityNotFound')}
        </div>
      )}

      {entity && (
        <NewTableKartotekaPanel
          entity={entity}
          onChangeFields={updateFields}
          readOnly={!canDesign}
          structureDirty={dirty}
        />
      )}
    </Modal>
  );
}
